// Le code di lavoro della Fatturazione (redesign 26/10/2026).
//
// Prima erano sei chip uguali, e «Storni» non c'era: gli storni stavano dentro
// «Da fatturare» con tutto il resto. Il 26/10 Davide ha trovato 18114942-A con
// la SF emessa e la ST mai fatta da tre mesi: nessun punto della pagina diceva
// «qui c'è un rimborso senza storno». Ora le code che chiedono un gesto stanno
// in prima fila, ciascuna col suo numero e con cosa succede dopo; le viste di
// archivio (Tutte, Storico, Escluse) restano a portata, più quiete.
import type { StatoTone } from "@/features/ordini/components/statoToni";
import { ChipFiltro } from "@/shared/ui/ChipFiltro";
import type { Counts } from "../types";

export type TabKey =
  | "tutte"
  | "da_fatturare"
  | "storni"
  | "in_attesa"
  | "da_inviare"
  | "escluse"
  | "storico";

interface Coda {
  key: TabKey;
  label: string;
  countKey: keyof Counts;
  /** Cosa succede a queste righe senza far niente: la riga sotto il numero. */
  poi: string;
}

const CODE: Coda[] = [
  {
    key: "da_fatturare",
    label: "Da fatturare",
    countKey: "da_fatturare",
    poi: "OM le emette alle 18",
  },
  { key: "storni", label: "Storni", countKey: "nc_pendenti", poi: "aperti, da emettere" },
  { key: "da_inviare", label: "Da inviare allo SdI", countKey: "da_inviare", poi: "F11 in Eureka" },
  {
    key: "in_attesa",
    label: "In attesa",
    countKey: "in_attesa",
    poi: "spedizione o pagamento",
  },
];

const ARCHIVIO: Array<{ key: TabKey; label: string; countKey: keyof Counts }> = [
  { key: "tutte", label: "Tutte", countKey: "tutte" },
  { key: "storico", label: "Storico", countKey: "fatturate" },
  { key: "escluse", label: "Escluse", countKey: "escluse" },
];

export const ETICHETTE_TAB: Record<TabKey, string> = Object.fromEntries(
  [...CODE, ...ARCHIVIO].map((t) => [t.key, t.label]),
) as Record<TabKey, string>;

export const COUNT_KEY: Record<TabKey, keyof Counts> = Object.fromEntries(
  [...CODE, ...ARCHIVIO].map((t) => [t.key, t.countKey]),
) as Record<TabKey, keyof Counts>;

/** Il tono di ogni coda: lo stesso del badge di stato delle sue righe. */
const TONO_CODA: Record<TabKey, StatoTone> = {
  da_fatturare: "nuovo",
  storni: "da-pagare",
  da_inviare: "bonifico",
  in_attesa: "da-pagare-atteso",
  tutte: "annullato",
  storico: "pagato",
  escluse: "non-ordinato",
};

/** La riga sotto il titolo della lista: cosa succede alle righe della coda. */
export function poiDellaCoda(tab: TabKey, scoperti: number): string | null {
  if (tab === "storni" && scoperti > 0)
    return `${scoperti} ${scoperti === 1 ? "rimborso" : "rimborsi"} senza storno`;
  return CODE.find((c) => c.key === tab)?.poi ?? null;
}

// Redesign 27/10: i chip della lista ordini (`ChipFiltro`). Le code che
// chiedono un gesto stanno prima del separatore, l'archivio dopo. «Cosa
// succede dopo» sta nel titolo del tasto e sotto il titolo della lista.
function Chip({
  id,
  label,
  n,
  attiva,
  allarme,
  title,
  onClick,
}: {
  id: TabKey;
  label: string;
  n: number | undefined;
  attiva: boolean;
  allarme?: string;
  title?: string;
  onClick: () => void;
}) {
  return (
    <ChipFiltro
      role="tab"
      tono={TONO_CODA[id]}
      attivo={attiva}
      onClick={onClick}
      title={title}
      conteggio={n ?? "–"}
      extra={allarme}
      evidenzia={Boolean(allarme)}
    >
      {label}
    </ChipFiltro>
  );
}

export function CodeLavoro({
  tab,
  onTab,
  counts,
  scoperti,
}: {
  tab: TabKey;
  onTab: (t: TabKey) => void;
  counts: Counts | null;
  /** Rimborsi senza storno in Eureka: accendono la coda Storni in rosso. */
  scoperti: number;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 max-md:-mx-3 max-md:flex-nowrap max-md:overflow-x-auto max-md:px-3 max-md:pb-1 max-md:[scrollbar-width:none] max-md:[&::-webkit-scrollbar]:hidden">
      <div
        role="tablist"
        aria-label="Code di lavoro"
        className="flex items-center gap-1 max-md:gap-2"
      >
        {CODE.map((c) => (
          <Chip
            key={c.key}
            id={c.key}
            label={c.label}
            n={counts?.[c.countKey]}
            attiva={tab === c.key}
            title={c.poi}
            allarme={
              c.key === "storni" && scoperti > 0
                ? `${scoperti} ${scoperti === 1 ? "rimborso" : "rimborsi"} senza storno`
                : undefined
            }
            onClick={() => onTab(c.key)}
          />
        ))}
      </div>
      <span
        role="separator"
        aria-orientation="vertical"
        className="mx-1 h-4 w-px shrink-0 bg-line-2"
      />
      <div role="tablist" aria-label="Archivio" className="flex items-center gap-1 max-md:gap-2">
        {ARCHIVIO.map((a) => (
          <Chip
            key={a.key}
            id={a.key}
            label={a.label}
            n={counts?.[a.countKey]}
            attiva={tab === a.key}
            onClick={() => onTab(a.key)}
          />
        ))}
      </div>
    </div>
  );
}
